import React from 'react';
import { Route, Navigate, useLocation } from 'react-router-dom';
import { AREA_CAPABILITIES } from '../areaCapabilities';
import {
  QHSEMainPage,
  DocumentControlPage,
  HelicopterOps,
  HelicopterConfigList,
  HelicopterRegistry,
  AssetRegistry,
  AssetDetail,
  ProjectFleet,
  FleetCategoryManager,
  DailyLogPage,
  MaintenanceLog,
  FieldOperations,
  FieldPlanningPage,
  FuelControlMain,
  SupervisorFuelCard,
  JmMain,
  JmRegisterPage,
  JmSettingsPage,
  OperationsChat,
  SPRegistryPage,
  MapView,
  CrewReportsPage,
  CrewReportForm,
  CrewReportFieldPage,
  LostTimePage,
  ClientDailyReportPage,
  WorkProjectionPage,
  DrillingOperationsDashboardPage,
  CDRArchivePage,
  NPTReportArchivePage,
} from '../lazyComponents';

// Back-compat redirect that preserves the tail (`/new`, `/:id`, query, hash)
// when an old URL is migrated to a new prefix. See Incident 15 / R32 in
// ARCHITECTURE.md for the four-step menu-route move recipe.
function PrefixRedirect({ from, to }) {
  const location = useLocation();
  const tail = location.pathname.startsWith(from)
    ? location.pathname.slice(from.length)
    : '';
  return <Navigate to={`${to}${tail}${location.search}${location.hash}`} replace />;
}

/**
 * QHSE hub, operations (fleet, helicopter, fuel, chat) and the drilling module
 * mounted under /drilling/* (re-namespaced 2026-05-25, see Incident 15).
 *
 * Old `/operations/field-*`, `/operations/drilling/*` and `/reports/*` routes
 * are kept as 302-style redirects so existing bookmarks, printed manuals and
 * external links keep working through the deprecation window.
 */
export function QhseOperationsRoutes({ withCapability, withAnyCapability }) {
  return (
    <>
      <Route path="/qhse" element={withCapability(AREA_CAPABILITIES.qhse, <QHSEMainPage />)} />
      <Route path="/qhse/settings" element={<Navigate to="/qhse?tab=settings" replace />} />
      <Route path="/qhse/personnel" element={<Navigate to="/qhse?tab=personnel" replace />} />
      <Route path="/qhse/incidents" element={<Navigate to="/qhse?tab=daily-logs" replace />} />
      <Route path="/qhse/audits" element={<Navigate to="/qhse?tab=daily-logs" replace />} />
      <Route path="/qhse/inspections" element={<Navigate to="/qhse?tab=daily-logs" replace />} />
      <Route path="/qhse/drills" element={<Navigate to="/qhse?tab=daily-logs" replace />} />
      <Route path="/qhse/observations" element={<Navigate to="/qhse?tab=daily-logs" replace />} />
      <Route path="/qhse/training" element={<Navigate to="/qhse?tab=daily-logs" replace />} />
      <Route path="/qhse/meetings" element={<Navigate to="/qhse?tab=daily-logs" replace />} />
      <Route path="/qhse/environment" element={<Navigate to="/qhse?tab=env-health" replace />} />
      <Route path="/qhse/medical" element={<Navigate to="/qhse?tab=env-health" replace />} />
      <Route path="/qhse/daily-logs" element={<Navigate to="/qhse?tab=comments" replace />} />
      <Route path="/qhse/waste" element={<Navigate to="/qhse?tab=env-health" replace />} />
      <Route path="/qhse/fuel" element={<Navigate to="/qhse?tab=env-health" replace />} />
      <Route path="/qhse/daily-report" element={<Navigate to="/qhse?tab=daily-report" replace />} />
      <Route path="/qhse/kpis" element={<Navigate to="/qhse?tab=kpis" replace />} />
      <Route path="/qhse/document-control" element={withCapability(AREA_CAPABILITIES.qhse, <DocumentControlPage />)} />

      {/* Operations — cross-area only (fleet, helicopter, fuel, chat). */}
      <Route path="/operations/fleet" element={withCapability(AREA_CAPABILITIES.operations, <ProjectFleet />)} />
      <Route path="/operations/fleet/registry" element={withCapability(AREA_CAPABILITIES.operations, <AssetRegistry />)} />
      <Route path="/operations/fleet/categories" element={withCapability(AREA_CAPABILITIES.operations, <FleetCategoryManager />)} />
      <Route path="/operations/fleet/registry/:assetId" element={withCapability(AREA_CAPABILITIES.operations, <AssetDetail />)} />
      <Route path="/operations/fleet/daily" element={withCapability(AREA_CAPABILITIES.operations, <DailyLogPage />)} />
      <Route path="/operations/fleet/maintenance" element={withCapability(AREA_CAPABILITIES.operations, <MaintenanceLog />)} />

      <Route path="/operations/helicopter" element={withCapability(AREA_CAPABILITIES.operations, <HelicopterConfigList />)} />
      <Route path="/operations/helicopter/registry" element={withCapability(AREA_CAPABILITIES.operations, <HelicopterRegistry />)} />
      <Route path="/operations/helicopter/new" element={withCapability(AREA_CAPABILITIES.operations, <HelicopterOps />)} />
      <Route path="/operations/helicopter/:configId" element={withCapability(AREA_CAPABILITIES.operations, <HelicopterOps />)} />

      <Route path="/operations/fuel-control" element={withCapability(AREA_CAPABILITIES.operations, <FuelControlMain />)} />
      <Route path="/operations/fuel-control/supervisor" element={withCapability(AREA_CAPABILITIES.operations, <SupervisorFuelCard />)} />
      <Route path="/operations/jm" element={withCapability(AREA_CAPABILITIES.operations, <JmMain />)} />
      <Route path="/operations/jm/register" element={withCapability(AREA_CAPABILITIES.operations, <JmRegisterPage />)} />
      <Route path="/operations/jm/settings" element={withCapability(AREA_CAPABILITIES.operations, <JmSettingsPage />)} />
      <Route path="/operations/chat" element={withCapability(AREA_CAPABILITIES.operations, <OperationsChat />)} />

      {/* Drilling — all drilling-area functionality under /drilling/*. */}
      <Route
        path="/drilling/dashboard"
        element={withCapability(AREA_CAPABILITIES.drilling, <DrillingOperationsDashboardPage />)}
      />
      <Route path="/drilling/sp-registry" element={withCapability(AREA_CAPABILITIES.drilling, <SPRegistryPage />)} />
      <Route path="/drilling/sp-registry/map" element={withCapability(AREA_CAPABILITIES.drilling, <MapView />)} />
      <Route path="/drilling/crew-reports" element={withCapability(AREA_CAPABILITIES.drilling, <CrewReportsPage />)} />
      <Route path="/drilling/crew-report/new" element={withCapability(AREA_CAPABILITIES.drilling, <CrewReportForm />)} />
      <Route path="/drilling/crew-report/:reportId" element={withCapability(AREA_CAPABILITIES.drilling, <CrewReportForm />)} />
      <Route path="/drilling/crew-report-field/*" element={withCapability(AREA_CAPABILITIES.drilling, <CrewReportFieldPage />)} />
      <Route path="/drilling/npt-log" element={withCapability(AREA_CAPABILITIES.drilling, <LostTimePage />)} />
      <Route path="/drilling/work-projection" element={withCapability(AREA_CAPABILITIES.drilling, <WorkProjectionPage />)} />
      <Route
        path="/drilling/progress-map"
        element={withAnyCapability([AREA_CAPABILITIES.drilling, AREA_CAPABILITIES.operations], <FieldOperations />)}
      />
      <Route
        path="/drilling/work-planning"
        element={withAnyCapability([AREA_CAPABILITIES.drilling, AREA_CAPABILITIES.operations], <FieldPlanningPage />)}
      />
      <Route path="/drilling/client-daily-report" element={withCapability(AREA_CAPABILITIES.drilling, <ClientDailyReportPage />)} />
      <Route
        path="/drilling/cdr-archive"
        element={withAnyCapability([AREA_CAPABILITIES.drilling, AREA_CAPABILITIES.operations], <CDRArchivePage />)}
      />
      <Route
        path="/drilling/npt-report-archive"
        element={withAnyCapability([AREA_CAPABILITIES.drilling, AREA_CAPABILITIES.operations], <NPTReportArchivePage />)}
      />

      {/* Back-compat redirects (deprecation window — remove once all clients use /drilling/* URLs). */}
      <Route path="/operations/field-operations" element={<Navigate to="/drilling/progress-map" replace />} />
      <Route path="/operations/field-planning" element={<Navigate to="/drilling/work-planning" replace />} />
      <Route path="/operations/drilling/work-projection" element={<Navigate to="/drilling/work-projection" replace />} />
      <Route path="/operations/drilling/sp-registry" element={<Navigate to="/drilling/sp-registry" replace />} />
      <Route path="/operations/drilling/sp-registry/map" element={<Navigate to="/drilling/sp-registry/map" replace />} />
      <Route path="/operations/drilling/crew-reports" element={<Navigate to="/drilling/crew-reports" replace />} />
      <Route path="/operations/drilling/crew-report/new" element={<Navigate to="/drilling/crew-report/new" replace />} />
      <Route
        path="/operations/drilling/crew-report/:reportId"
        element={<PrefixRedirect from="/operations/drilling/crew-report" to="/drilling/crew-report" />}
      />
      <Route
        path="/operations/drilling/crew-report-field/*"
        element={<PrefixRedirect from="/operations/drilling/crew-report-field" to="/drilling/crew-report-field" />}
      />
      <Route path="/operations/drilling/npt-log" element={<Navigate to="/drilling/npt-log" replace />} />
      <Route path="/operations/drilling/client-daily-report" element={<Navigate to="/drilling/client-daily-report" replace />} />
      <Route path="/reports/cdr-archive" element={<Navigate to="/drilling/cdr-archive" replace />} />
      <Route path="/reports/npt-report-archive" element={<Navigate to="/drilling/npt-report-archive" replace />} />
    </>
  );
}
